import { Component, OnInit } from '@angular/core';
import { Modal } from '@app/core/types/modal-type';
import {
  AverageQueryMeteredDataResultDto,
  MeteredDataService,
  QueryEditMissingMeteredDataResultCollectionDto,
  QueryMeteredDataResultDto,
  SubmitMeteredDataDto,
  SubmitMissingMeteredDataDto
} from '@app/api/dmc';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { TimeseriesFormComponent } from '@shared/components/timeseries/timeseries-form/timeseries-form.component';
import { InputDateDirective } from '@shared/components/input/date/input-date.directive';
import { ToggleComponent } from '@shared/components/input/toggle/toggle.component';
import { FormApiService } from '@app/core/services/form-api.service';
import { HasUnsavedChanges } from '@app/core/guards/unsaved-changes.guard';

@Component({
  selector: 'app-edit-timeseries-modal',
  imports: [
    InputGroupComponent,
    LabelDirective,
    TranslatePipe,
    ReactiveFormsModule,
    TimeseriesFormComponent,
    InputDateDirective,
    FormsModule,
    ToggleComponent
  ],
  templateUrl: './edit-timeseries-modal.component.html',
  styleUrl: './edit-timeseries-modal.component.css'
})
export class EditTimeseriesModalComponent implements Modal, OnInit, HasUnsavedChanges {
  public hasUnsavedChanges(): boolean {
    return this.form.dirty;
  }

  public form: FormGroup = new FormGroup({
    wantsDataLocked: new FormControl(false),
    meteredData: new FormControl(null),
    estimationMethod: new FormControl(null),
    meteringPointIdentifications: new FormControl(null, Validators.required),
  })

  public date?: string;

  public timeSeries?: QueryMeteredDataResultDto[];
  public timeSeriesAverage?: AverageQueryMeteredDataResultDto[];

  /** Element Data **/
  public element?: any;

  constructor(
    private readonly meteredDataService: MeteredDataService,
    private readonly formApiService: FormApiService,
  ) {

  }

  public ngOnInit(): void {
    this.fetchTimeSeries();

    if (this.element?.meteringPointIdentification) {
      this.form.get('meteringPointIdentifications')?.setValue([this.element.meteringPointIdentification]);
    }

    if (this.element?.dayOfWork) {
      this.date = this.formatDayOfWorkAsLocalDate(this.element.dayOfWork);
    }
  }

  private fetchTimeSeries(): void {
    if (!this.element?.meteringPointIdentification || !this.element.dayOfWork) return;

    this.meteredDataService.meteredDataQueryEditMissingMeteredDataPost({
      meteringPointIdentifications: [this.element.meteringPointIdentification],
      dayOfWork: this.element.dayOfWork,
    }).subscribe((result: QueryEditMissingMeteredDataResultCollectionDto) => {
        this.timeSeries = result.results?.reverse();
        this.timeSeries = this.getLatestTimeseries();
        this.timeSeries.forEach(t => t.consumption = t.consumption ?? 0);
        this.timeSeriesAverage = result.average?.reverse();
    })
  }

  private formatDayOfWorkAsLocalDate(value: string): string {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private getLatestTimeseries(): QueryMeteredDataResultDto[] {
    if (!this.timeSeries || this.timeSeries.length === 0) return [];
    const maxPackageId = Math.max(...this.timeSeries.map(ts => ts.packageId ? Number.parseInt(ts.packageId, 10): 0));
    return this.timeSeries.filter(ts => ts.packageId ? Number.parseInt(ts.packageId, 10) === maxPackageId : false);
  }

  public submit(): void {
    if (this.form.valid && this.timeSeries) {
      const ts: SubmitMeteredDataDto[] = this.timeSeries.map((element: QueryMeteredDataResultDto) => ({
            readingTs: element.readingTs,
            consumption: element.consumption ?? undefined,
            readingType: element.consumptionType ?? element.readingType
          }))

      this.form.get('meteredData')?.setValue(ts);

      this.formApiService.submit(
        this.form,
        (data: SubmitMissingMeteredDataDto) => this.meteredDataService.meteredDataSubmitMissingMeteredDataPost(data),
        {
          preprocess: (data: SubmitMissingMeteredDataDto) => ({
            ...data,
            meteredData: this.form.get('meteredData')?.value,

          }),
          closeModal: true,
          successMessage: 'timeSeries.missing.success'
        }
      )
    }
  }
}
